import React, { useState } from "react";
import { X, UserPlus, Save } from "lucide-react";
import { toast } from "sonner";
import { adminCreateMember, adminUpdateMember, apiError } from "../../lib/adminApi";
import { Field, inputCls, btnPrimary, btnGhost, fmtDate } from "./ui";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!#";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

export const EMAIL_DOMAIN = "basiq.com";

const DURATIONS = [["", "Sem expiração"], ["30", "30 dias"], ["90", "90 dias"], ["180", "6 meses"], ["365", "1 ano"], ["custom", "Data específica"]];

// Create (user=null) or edit (user set) a founding member.
export default function MemberDialog({ user, onClose, onSaved }) {
  const editing = !!user;
  const [name, setName] = useState(user?.name || "");
  const [emailUser, setEmailUser] = useState(user?.email ? user.email.split("@")[0] : "");
  const email = editing ? user.email : `${emailUser.trim().toLowerCase()}@${EMAIL_DOMAIN}`;
  const [password, setPassword] = useState(editing ? "" : genPassword());
  const [plan, setPlan] = useState(user?.effective_plan || user?.plan || "pro");
  const [duration, setDuration] = useState(editing ? (user?.plan_expires_at ? "custom" : "") : "365");
  const [expiresAt, setExpiresAt] = useState(user?.plan_expires_at ? user.plan_expires_at.slice(0, 10) : "");
  const [note, setNote] = useState(user?.note || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const expiry = duration === "custom" ? { expires_at: expiresAt ? `${expiresAt}T23:59:59+00:00` : null } : duration ? { duration_days: Number(duration) } : {};
      if (editing) {
        const payload = { name, plan, note, ...expiry };
        if (!duration && plan !== "free") payload.clear_expiry = true;
        if (password) payload.password = password;
        const u = await adminUpdateMember(user.user_id, payload);
        toast.success("Membro atualizado");
        onSaved(u);
      } else {
        const u = await adminCreateMember({ name, email, password, plan, note, ...expiry });
        toast.success("Membro criado");
        onSaved(u, { email, password });
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4" data-testid="member-dialog">
      <form onSubmit={submit} className="w-full max-w-lg rounded-3xl bg-[#111827] ring-1 ring-white/10 p-6 sm:p-7 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">{editing ? <Save size={18} /> : <UserPlus size={18} />} {editing ? "Editar membro" : "Novo membro-fundador"}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="member-dialog-close"><X size={18} /></button>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Field label="Nome"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} required data-testid="member-name" /></Field>
          <Field label="E-mail">
            <div className={`flex items-center rounded-xl bg-[#0B0F19] ring-1 ring-white/10 focus-within:ring-2 focus-within:ring-[#38BDF8] ${editing ? "opacity-60" : ""}`}>
              <input value={emailUser} onChange={(e) => setEmailUser(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ""))} className="flex-1 min-w-0 h-10 bg-transparent outline-none px-3 text-[13.5px] text-slate-100 placeholder:text-slate-500" placeholder="nome.sobrenome" required pattern="[a-zA-Z0-9._-]{2,}" disabled={editing} data-testid="member-email" />
              <span className="pr-3 text-[13px] text-slate-400 font-mono-jb select-none" data-testid="member-email-domain">@{EMAIL_DOMAIN}</span>
            </div>
          </Field>
          <Field label={editing ? "Nova senha (opcional)" : "Senha"}>
            <div className="flex gap-2">
              <input value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} font-mono-jb`} required={!editing} minLength={6} placeholder={editing ? "deixe vazio para manter" : ""} data-testid="member-password" />
              <button type="button" onClick={() => setPassword(genPassword())} className={`${btnGhost} px-3 shrink-0`} data-testid="member-gen-password">Gerar</button>
            </div>
          </Field>
          <Field label="Plano">
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className={inputCls} data-testid="member-plan">
              <option value="free">Free</option><option value="standard">Standard</option><option value="pro">Pro</option>
            </select>
          </Field>
          {plan !== "free" && (
            <Field label="Duração do plano">
              <select value={duration} onChange={(e) => setDuration(e.target.value)} className={inputCls} data-testid="member-duration">
                {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          )}
          {plan !== "free" && duration === "custom" && (
            <Field label="Expira em"><input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputCls} required data-testid="member-expires" /></Field>
          )}
          <div className="sm:col-span-2">
            <Field label="Observação interna"><input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="ex.: fundador #3, indicado pelo Roberto" data-testid="member-note" /></Field>
          </div>
        </div>
        {editing && user?.plan_expires_at && <p className="mt-3 text-[12px] text-slate-400">Expiração atual: {fmtDate(user.plan_expires_at)}. Ao expirar, o plano volta para Free automaticamente.</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancelar</button>
          <button type="submit" disabled={busy} className={btnPrimary} data-testid="member-submit">{busy ? "Salvando…" : editing ? "Salvar alterações" : "Criar membro"}</button>
        </div>
      </form>
    </div>
  );
}
